import React, { useEffect, useMemo, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';
import { LabelsResultsData, SummaryDataItem } from '../../types/types';
import { useSelector } from 'react-redux';
import { useAnalyticsUrlProps } from '../../../CreativeAnalytics/PivotReport/hooks';
import { useSearchParams } from '../../../../components/Hooks/search-params';
import { DetailData } from './DetailData/DetailData';
import { getColumns, SummaryTableWrapper } from './SummaryTable';
import { RootState } from '../../../../store/types';
import { useSummaryCols } from './SummaryTable/columns';
import { useShareStaticDetailData } from '../../hooks/useShareStaticDetailData';
import { useTrackEvent } from '../../../CreativeAnalytics/hooks/useTrackEvent';
import { ShareReport } from '../../../DNA/ShareReport';
import {
  REPORT_BUILDER,
  SUMMARY_DATA_STATIC_EXPORT,
} from '../../../../store/UserActivity/constants';
import {
  filterVisualsBySelectedRow,
  useCategoryData,
  useSafeLabel,
  useVisualsData,
} from './DetailData/hooks';
import { useSaveOverridesApi } from '../../../CreativeAnalytics/Explorer/api';
import { DATASET_MAPPING } from './DetailData/DataMode';
import { getColumnOptions } from './DetailData/utils';
import { StaticReportModal } from '../StaticReportModal';
import ReportCard from '../../../ReportCard';
// import { ReportCard } from '../ReportCard';

interface ResultsProps {
  debug_data: any;
  detail_data: any;
  summary_data: any;
  master_mappings: any[];
  visual_id_data: any[];
  dateRange: any;
  renderAnalyticsToggle: () => React.ReactNode;
  darwinIqApi: any;
  selectedBlockIds: any[];
  allBlocks: any[];
}

export const Results: React.FC<ResultsProps> = ({
  debug_data,
  detail_data,
  summary_data,
  master_mappings,
  visual_id_data,
  dateRange,
  renderAnalyticsToggle,
  darwinIqApi,
  selectedBlockIds,
  allBlocks,
  runQueryLoading,
}) => {
  const trackEvent = useTrackEvent();
  const isROAS = useSelector((state: RootState) => state.ViewSettings.isROAS);
  const {
    dataset,
    selectedRowId,
    mode,
    summary_columns,
    categories: selectedCategories,
  } = useAnalyticsUrlProps();
  const { setParams } = useSearchParams();
  const [sort, setSort] = useState({
    sortBy: 'total_spend',
    desc: true,
  });

  const datasetConfig = DATASET_MAPPING[dataset] || {};

  const handleFetchOnRowClick = (
    id: string,
    refresh_insights: boolean = false
  ) => {
    const row = summary_data.find((item) => item.id === id);
    const allowDataMode =
      summary_data.length === 1 ||
      (!!row?.ad_name_group && row?.ad_name_group !== 'No Grouping');

    if (!allowDataMode) return darwinIqApi.clearData();

    const selected_segment = allBlocks?.find((block) => {
      return block.name === row?.ad_name_group;
    });

    const isDataLoaded = !!summary_data?.length;
    const isNoFiltering = summary_data?.length === 1;
    const isSegmentSelected = !!row && row.ad_name_group !== 'No Grouping';
    const isValidSegment =
      !!row?.ad_name_group && (!row?.label_name || row?.label_name === 'None');

    if (isDataLoaded && (isNoFiltering || isValidSegment)) {
      darwinIqApi.fetchDarwinIqData(
        dateRange.start,
        dateRange.end,
        selected_segment,
        refresh_insights
      );
    }
  };

  const colOpts = getColumnOptions(isROAS);

  const setSelectedRowId = (id: string, rest?: { summary_columns: string[] }) =>
    setParams({ selectedRowId: id, ...rest });

  const selectedRow: SummaryDataItem = useMemo(() => {
    return summary_data.find((item) => item.id === selectedRowId);
  }, [summary_data, selectedRowId]);

  const selectedColumns = useMemo(
    () => getColumns(summary_columns, isROAS, master_mappings),
    [summary_columns, isROAS, master_mappings]
  );
  const summaryCols = useSummaryCols(isROAS, summary_data, master_mappings);

  useEffect(() => {
    if (summary_data.length > 0) {
      const sortedBySpend = summary_data.sort(
        (a, b) => b.total_spend - a.total_spend
      );
      let defaultRow = sortedBySpend[0].id;

      if (summary_data.length > 1) {
        defaultRow =
          sortedBySpend.find((item) => item.ad_name_group !== 'No Grouping')
            ?.id || sortedBySpend[0].id;
      }

      const dataHasLabelName = summary_data.some(
        (group) => !!group.label_name && group.label_name !== 'None'
      );

      let updated_cols = [...summary_columns];

      if (dataHasLabelName) {
        if (!summary_columns.includes('label_name'))
          updated_cols.push('label_name');

        setParams({ summary_columns: updated_cols });
      }

      if (!selectedRow) {
        setSelectedRowId(defaultRow, { summary_columns: updated_cols });
      }
      handleFetchOnRowClick(defaultRow ?? selectedRowId, false);
    }
  }, [summary_data]);

  const handleRowSelect = (id: string) => {
    setSelectedRowId(id);
    handleFetchOnRowClick(id, false);
  };

  const visuals = useVisualsData(detail_data, selectedRow, sort);

  const { selectedLabel, setSelectedLabel, safeSetSelectedLabel } =
    useSafeLabel();

  const { selectedCategoryData, data, dataByCategory } = useCategoryData(
    selectedLabel,
    darwinIqApi.data,
    datasetConfig,
    selectedCategories
  );

  const visualIdData = useMemo(() => {
    const filtered = visual_id_data.filter(
      filterVisualsBySelectedRow(selectedRow)
    );

    return filtered?.map((obj) => ({
      ...obj,
      array_of_ad_names: obj.array_of_ad_names || obj['Ad Name'],
    }));
  }, [visual_id_data, selectedRow]);

  const {
    shouldShowShareModal,
    openShareModal,
    closeShareModal,
    generateReport,
    sharedReportId,
    loadingShareReport,
    staticReportSections,
    toggleSection,
  } = useShareStaticDetailData(isROAS);

  const ShareModal = shouldShowShareModal ? (
    <StaticReportModal
      close={closeShareModal}
      submit={({ report_name, report_description }) => {
        trackEvent(REPORT_BUILDER, SUMMARY_DATA_STATIC_EXPORT, {});
        generateReport({
          report_name,
          report_description,
          start_date: dateRange.start,
          end_date: dateRange.end,
          data,
          visuals,
          visual_id_data: visualIdData,
          columns: selectedColumns,
          sort,
          columnOptions: colOpts,
          master_mappings,
          summary_data,
          insightsData: darwinIqApi.data?.insights ?? [],
        });
      }}
      sharedReportId={sharedReportId}
      loading={loadingShareReport}
      categorySettings={{}}
      location={'report_builder'}
      sharePayloadError={null}
      staticReportSections={staticReportSections}
      toggleSection={toggleSection}
    />
  ) : null;

  const [selectedVisual, setSelectedVisual] = useState<any>(null);

  const handleVisualClick = (visual: any) => {
    setSelectedVisual(visual);
  };

  const handleCloseReportCard = () => {
    setSelectedVisual(null);
  };

  return (
    <div className="lp-results">
      <SummaryTableWrapper
        data={summary_data}
        master_mappings={master_mappings}
        exportFileName="summary_data"
        onRowSelect={handleRowSelect}
        selectedRowId={selectedRowId}
        dateRange={dateRange}
        columns={summaryCols}
        selectedColumns={selectedColumns}
        summary_columns={summary_columns}
        isROAS={isROAS}
        ShareModal={ShareModal}
        openShareModal={openShareModal}
      />

      <DetailData
        exportFileName="detail_data"
        renderAnalyticsToggle={renderAnalyticsToggle}
        darwinIqData={darwinIqApi.data}
        fetchAnalyticsData={(shouldRefresh) =>
          handleFetchOnRowClick(selectedRowId, shouldRefresh)
        }
        allowDataMode={
          summary_data.length === 1 ||
          (!!selectedRow?.ad_name_group &&
            selectedRow?.ad_name_group !== 'No Grouping')
        }
        master_mappings={master_mappings}
        summaryCols={summaryCols}
        selectedSummaryColumns={selectedColumns}
        ShareModal={ShareModal}
        visual_id_data={visualIdData}
        onVisualClick={handleVisualClick}
        {...{
          detail_data,
          selectedRow,
          dateRange,
          isROAS,
          darwinIqApi,
          mode,
          visual_id_data,
          summary_data,
          sort,
          setSort,
          selectedLabel,
          dataByCategory,
          selectedCategoryData,
          safeSetSelectedLabel,
          openShareModal,
          visuals,
          runQueryLoading,
        }}
      />

      {!!selectedVisual && (
        <ReportCard
          visual={selectedVisual}
          categoryPerformanceData={
            darwinIqApi.data?.category_performance_data || []
          }
          loading={darwinIqApi.loading}
          onClose={() => setSelectedVisual(null)}
          iterationsData={[]} // optional
          start_date={dateRange.start}
          end_date={dateRange.end}
        />
      )}
    </div>
  );
};
